/**
 * Per-card wrapper. Pure renderer of (status, index, viewMode) → animation.
 *
 * - Reads the card's status from the controller (via props).
 * - Computes its animate target via `deckAnimate`.
 * - Signals exit-animation completion back to the controller via Motion's
 *   `onAnimationComplete`. No setTimeouts, no parallel `animate()` playback.
 * - Provides the per-card coordination context so swipe-aware children can
 *   notify the controller about swipe-pending / swipe-resolve events.
 */
import { motion } from "motion/react";
import { useMemo, useRef } from "react";
import { match } from "ts-pattern";

import { isExitTargetDefinition } from "@/web/lib/motion-helpers";
import { cn } from "@/web/lib/styling";
import {
  deckAnimate,
  deckTransition,
  STACK_CARD_SIZE_CLASS,
  type CardStatus,
  type ViewMode,
} from "@/web/routes/_protected/smb/-dashboard/-deckAnimate";
import {
  DeckCardCoordinationContext,
  type DeckCardCoordination,
} from "@/web/routes/_protected/smb/-dashboard/-DeckContext";
import {
  isRecycleOnComplete,
  type SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

export function DeckCard({
  card,
  index,
  total,
  viewMode,
  onSkip,
  canSkip,
  onComplete,
  onRecycle,
  beginSwipePending,
  resolveSwipe,
  onExitAnimationComplete,
}: {
  card: { id: string; status: CardStatus; definition: SmbActionDefinition };
  index: number;
  total: number;
  viewMode: ViewMode;
  onSkip: (id: string) => void;
  canSkip: boolean;
  onComplete: (id: string, result?: unknown) => void;
  /** Recycle the card to the back instead of dismissing it (opt-in via result). */
  onRecycle: (id: string) => void;
  beginSwipePending: (id: string, direction: "left" | "right") => void;
  resolveSwipe: (id: string, outcome: "committed" | "rejected") => void;
  onExitAnimationComplete: (id: string) => void;
}) {
  const { id, status, definition } = card;

  // Status ref so the onAnimationComplete closure always reads the latest
  // status — Motion fires the callback async, and we must only act on the
  // current render's exit state.
  const statusRef = useRef(status);
  statusRef.current = status;

  const target = deckAnimate({ status, index, total, viewMode });
  const transition = deckTransition({ status, viewMode });

  const canSkipCard = definition.canSkip && canSkip;

  const coordination: DeckCardCoordination = useMemo(
    () => ({
      beginSwipePending: (direction) => beginSwipePending(id, direction),
      resolveSwipe: (outcome) => resolveSwipe(id, outcome),
      skip: canSkipCard ? () => onSkip(id) : null,
      viewMode,
    }),
    [id, beginSwipePending, resolveSwipe, canSkipCard, onSkip, viewMode],
  );

  const isStackedBehind = viewMode === "stack" && index > 0 && status.kind === "idle";

  return (
    <motion.div
      data-deck-card-id={id}
      data-deck-card-status={status.kind}
      data-back-layer={viewMode === "stack" && index >= 4}
      layout
      layoutDependency={`${viewMode}-${total}`}
      inert={isStackedBehind ? true : undefined}
      initial={false}
      animate={target}
      transition={transition}
      // Pin the transform origin to center, inline, from the very first paint.
      //
      // Why this matters: the back cards in the stack scale down. Motion only
      // writes `transform-origin` inline *once it has run an animation/layout
      // projection* — and that value is `50% 50%` (center). On the initial
      // render (StrictMode off, i.e. production) Motion writes `transform` but
      // not `transform-origin`, so the element falls back to whatever CSS says.
      // With a `origin-bottom` utility on the className, first paint scaled the
      // cards about their *bottom* edge, which pushes each card's top edge down
      // and very nearly cancels the upward `STACK_STEP` translate — the stack
      // rendered almost flat. The first view-mode toggle or skip kicked Motion
      // into its animated render path, which overwrote the origin with center
      // and "healed" the offset. StrictMode (dev) masked the bug because its
      // mount/unmount/remount forced that second, center-origin paint for free.
      //
      // Declaring the origin here makes first paint and every subsequent paint
      // agree on center — the pose the design was approved against.
      style={{ transformOrigin: "center" }}
      onAnimationComplete={(definition) => {
        // Motion fires this callback for *any* settled animation on this
        // element, including layout springs and sibling shifts. Both of
        // our exit targets animate `opacity` to 0; gating on that lets us
        // distinguish the exit `animate` settle from spurious fires that
        // would otherwise remove the card mid-peel.
        if (!isExitTargetDefinition(definition)) return;
        const current = statusRef.current.kind;
        if (current === "exit-skip" || current === "exit-complete") {
          onExitAnimationComplete(id);
        }
      }}
      className={cn(
        // `transform-origin` is set inline via the `style` prop above (center,
        // consistently from first paint) — do not add an `origin-*` utility
        // here, it only applies before Motion's first animated paint and
        // reintroduces the flat-stack first-render bug.
        "data-[back-layer=true]:**:data-[slot=smb-action-card-flipper]:shadow-none",
        match(viewMode)
          // Stack: the motion.div is itself card-sized and absolute-centered
          // via `inset-0 m-auto` (no transform-based centering, so the `x`
          // from `deckAnimate` is purely the skip translation). This makes
          // `x: -118%` resolve to 118% of the *card* width, not the
          // container width — on desktop the card no longer flies across
          // the whole viewport.
          .with("stack", () =>
            // Reserve `4rem` (64px) of vertical breathing room: the back
            // stack peeks ~60px above the centered front card via
            // `STACK_STEP`, so we need ≥ 60px set aside to keep the peek
            // from clipping the page header on short viewports.
            cn("absolute mx-auto transform-3d", STACK_CARD_SIZE_CLASS),
          )
          // Same sizing as stack so the toggle is a pure position change —
          // no size morph mid-animation. Only the positioning (absolute
          // mx-auto vs in-flow) differs between the two modes.
          .with("grid", () => cn("shrink-0", STACK_CARD_SIZE_CLASS))
          .exhaustive(),
      )}
    >
      <DeckCardCoordinationContext value={coordination}>
        {definition.render({
          onSkip: canSkipCard ? () => onSkip(id) : undefined,
          onComplete: (result) =>
            isRecycleOnComplete(result) ? onRecycle(id) : onComplete(id, result),
        })}
      </DeckCardCoordinationContext>
    </motion.div>
  );
}
